import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import NavigationHeader from "@/components/NavigationHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { getCanonicalUrl } from "@/config/seo.config";
import logo from "@/assets/logo.webp";

export default function NotFound() {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <SEO
        page="home"
        title="404 | Page Not Found | Vet Assist"
        description="The page you are looking for does not exist."
        noindex={true}
        canonical={getCanonicalUrl("/")}
      />
      <NavigationHeader />

      <main className="mt-24 flex flex-1 items-center justify-center px-6 py-24">
        <div className="mx-auto max-w-xl text-center">
          {/* Animated 404 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8">
            <h1 className="text-[150px] leading-none font-bold tracking-tight md:text-[200px]">
              <span className="text-[#2546A1]">4</span>
              <span className="text-[#7CB342]">0</span>
              <span className="text-[#2546A1]">4</span>
            </h1>
          </motion.div>

          {/* Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}>
            <h2 className="mb-4 text-2xl font-bold text-gray-900 md:text-3xl">Page Not Found</h2>
            <p className="mx-auto mb-8 max-w-md text-base text-gray-600 md:text-lg">
              Oops! The page you're looking for doesn't exist or has been moved. Let's get you back
              on track.
            </p>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              asChild
              className="h-12 rounded-xl bg-[#2546A1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:bg-[#1d3782] hover:shadow-xl">
              <Link to="/" className="flex items-center gap-2">
                <Home className="h-5 w-5" />
                Back to Home
              </Link>
            </Button>

            <Button
              asChild
              variant="outline"
              className="h-12 rounded-xl border-2 border-gray-300 px-8 py-3 font-semibold text-gray-700 transition-all hover:border-[#2546A1] hover:text-[#2546A1]">
              <Link
                to="#"
                onClick={() => window.history.back()}
                className="flex items-center gap-2">
                <ArrowLeft className="h-5 w-5" />
                Go Back
              </Link>
            </Button>
          </motion.div>

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-16">
            <img src={logo} alt="VetAssist" className="mx-auto h-10 w-auto opacity-50" />
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
